import { useQuery } from "@tanstack/react-query"
import { client } from "@/api"

/** One template's send breakdown for the Stats tab's per-template table. */
export interface EmailTemplateStat {
  type: string
  total: number
  delivered: number
  bounced: number
  complained: number
  failed: number
  queued: number
  sent: number
}

export interface SesReputationMetric {
  value: number | null
  max: number | null
  datapoints: number
  latestTimestamp: string | null
}

export interface SesReputationStats {
  configured: boolean
  region: string
  windowDays: number
  periodSeconds: number
  bounceRate: SesReputationMetric
  complaintRate: SesReputationMetric
  sends: number | null
  deliveries: number | null
  bounces: number | null
  complaints: number | null
  rejects: number | null
  thresholds: {
    bounceRateReview: number
    complaintRateReview: number
  }
  error?: string
}

/** Admin Email → Stats payload: global rollup + per-template breakdown. */
export interface EmailStats {
  sinceDays: number
  total: number
  byStatus: Record<string, number>
  deliveredRate: number
  bounceRate: number
  complaintRate: number
  byTemplate: EmailTemplateStat[]
  sesReputation: SesReputationStats
}

// Stats for the admin Email → Stats tab. Includes account-level SES reputation
// from CloudWatch plus our logged transactional send rollup and per-template
// breakdown from email_events. `window` is in days (default 30).
export function useEmailStats(windowDays = 30) {
  return useQuery({
    queryKey: ["admin", "email-stats", windowDays],
    queryFn: async () => {
      const response = await client.admin.email.deliverability.$get({
        query: { window: windowDays },
      })
      if (!response.ok) {
        throw new Error("Failed to fetch email stats")
      }
      return response.json() as Promise<EmailStats>
    },
  })
}
